import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { Intercom } from '@capacitor-community/intercom';
import { Capacitor } from '@capacitor/core';
import { isNil } from 'ramda';
import {
	IntercomProvider as ExternalIntercomProvider,
	useIntercom as useIntercomExternal,
} from 'react-use-intercom';

import { useTeam } from '../TeamProvider';

interface IIntercomContext {
	openIntercom: () => void;
}

const IntercomContext = createContext<IIntercomContext>({
	openIntercom: () => void 0,
});

export function useIntercom() {
	const context = useContext(IntercomContext);
	if (isNil(context)) {
		throw new Error(`useIntercom must be used within an IntercomProvider`);
	}

	const { openIntercom } = context;
	return {
		openIntercom,
	};
}

/**
 * IntercomProvider is instantiated when user logs in, and destroyed when user logs out.
 */
export const InternalIntercomProvider: React.FC = ({ children }) => {
	const { trackEvent } = useAnalytics();
	const { user, isAuthenticated } = useAuth();
	const { team } = useTeam();
	const [intercomInitialized, setIntercomInitialized] = useState(false);
	const { boot, update, show, shutdown } = useIntercomExternal();
	const isCapacitor = Capacitor.isNativePlatform();

	useEffect(() => {
		const identifyUser = async () => {
			const userAttributes = {
				email: user.email,
				name: user.displayName || 'Not Display Name',
				customAttributes: {
					teamId: team.id,
				},
			};

			if (isCapacitor) {
				if (!intercomInitialized) {
					setIntercomInitialized(true);
					Intercom.registerIdentifiedUser({ userId: user.id });
				} else {
					Intercom.updateUser(userAttributes);
				}
			} else {
				if (!intercomInitialized) {
					setIntercomInitialized(true);
					boot({
						hideDefaultLauncher: true,
						...userAttributes,
						userId: user.id,
					});
				} else {
					update({ ...userAttributes, userId: user.id });
				}
			}
		};

		if (isAuthenticated) {
			identifyUser();
		}
	}, [
		user.id,
		user.email,
		team.id,
		user.displayName,
		intercomInitialized,
		isCapacitor,
		isAuthenticated,
		boot,
		update,
	]);

	useEffect(() => {
		return () => {
			if (isCapacitor) {
				Intercom.logout();
			} else {
				shutdown();
			}
		};
	}, [isCapacitor, shutdown]);

	const openIntercom = useCallback(() => {
		if (isCapacitor) {
			Intercom.displayMessenger();
		} else {
			show();
		}
		trackEvent('open_intercom', {});
	}, [isCapacitor, show, trackEvent]);

	return (
		<IntercomContext.Provider value={{ openIntercom }}>
			{children}
		</IntercomContext.Provider>
	);
};

const IntercomProvider: React.FC = ({ children }) => {
	const {
		integrations: { intercom },
	} = useEnvironment();

	return (
		<ExternalIntercomProvider appId={intercom.appId}>
			<InternalIntercomProvider>{children}</InternalIntercomProvider>
		</ExternalIntercomProvider>
	);
};

export default IntercomProvider;
